import { notifyError } from "@/lib/notify-error";
import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const MIN_FEEDBACK_LENGTH = 10;
const MAX_FEEDBACK_LENGTH = 4000;

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitFeedback = useMutation(api.feedback.submit);

  const trimmedMessage = message.trim();
  const canSubmit = trimmedMessage.length >= MIN_FEEDBACK_LENGTH && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await submitFeedback({
        message: trimmedMessage,
        pathname: window.location.pathname,
        userAgent: window.navigator.userAgent,
      });
      toast.success("Feedback sent");
      setMessage("");
      onOpenChange(false);
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Unable to send feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-2 sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Tell us what is working, what is confusing, or what you would like improved.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-2">
            <Label htmlFor="feedback-message">Feedback</Label>
            <Textarea
              id="feedback-message"
              value={message}
              maxLength={MAX_FEEDBACK_LENGTH}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Share feedback, report an issue, or suggest an improvement..."
              className="min-h-36 resize-y"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span />
              <span>{message.length}/{MAX_FEEDBACK_LENGTH}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : null}
            Send Feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
